import { PlatformClient } from '@speechifyinc/platform-public';
import { type PublicGetVoiceResponse } from '@speechifyinc/platform-public/api/resources/vms';

import { PlatformServiceFacade } from './_base';
import { LogError } from './decorators/error';
import { PlatformVoice } from './types';

const ALL_SCOPES = ['reader-app', 'reader-app:onboarding', 'reader-app:recommended'] as const;

export type VMSVoiceScope = (typeof ALL_SCOPES)[number];
export type VMSVoice = PublicGetVoiceResponse;

export interface VMSVoiceSpecResponse {
  allTabsVoices: PlatformVoice[];
  recommendedVoices: PlatformVoice[];
  defaultVoice: PlatformVoice;
  fallbackVoice: PlatformVoice;
}
export class PlatformVMSFacade extends PlatformServiceFacade {
  private static _singleton: PlatformVMSFacade;
  constructor(client: PlatformClient) {
    super(client);
    PlatformVMSFacade._singleton = this;
  }

  static override get className(): string {
    return 'PlatformVMSFacade';
  }

  private get vmsService() {
    return this.client.vms;
  }

  static override get singleton(): PlatformVMSFacade {
    return PlatformVMSFacade._singleton;
  }

  @LogError
  public async getPublicVoicesByScopes(): Promise<Record<VMSVoiceScope, VMSVoice[]>> {
    return this.vmsService.publicVoice.getVoicesByScopes({ scopes: [...ALL_SCOPES] }, { headers: await this.getHeaders() });
  }

  @LogError
  public async getPersonalVoices() {
    return this.vmsService.personalVoice.getVoices({ headers: await this.getHeaders() });
  }

  @LogError
  public async deletePersonalVoice(slug: string) {
    return this.vmsService.personalVoice.deleteVoice(slug, { headers: await this.getHeaders() });
  }

  private filterRecommendedVoice(voices: PlatformVoice[]) {
    // @TODO(tony): Hardcoding as of now to match iOS, will remove once order is aligned on all platform
    const sortedVoices = voices
      .toSorted((a, b) => {
        const order = ['snoop', 'gwyneth', 'mrbeast', 'bwyneth', 'cliff', 'nate'];
        const indexA = order.includes(a.slug) ? order.indexOf(a.slug) : 6;
        const indexB = order.includes(b.slug) ? order.indexOf(b.slug) : 6;
        return indexA - indexB;
      })
      .slice(0, 6);
    sortedVoices[3].tags.push('label:narrator');
    sortedVoices[5].tags.push('label:narrator');
    return sortedVoices;
  }

  @LogError
  public async getPublicVoices(voicesByScopes: Record<string, PublicGetVoiceResponse[]>): Promise<VMSVoiceSpecResponse> {
    const allTabsVoices = voicesByScopes['reader-app'];

    const recommendedVoices = this.filterRecommendedVoice(voicesByScopes['reader-app:recommended']);

    const defaultVoice = allTabsVoices[0];
    const fallbackVoice = allTabsVoices[0];
    return {
      allTabsVoices,
      recommendedVoices,
      defaultVoice,
      fallbackVoice
    };
  }
}
